import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { ExpenseData, TimeEntryData } from '@/types/api';

const CLIENT_KEY = (clientId: number, page: number) => ['expenses', 'client', clientId, page];

export function useExpenses(clientId: number | undefined, page = 1) {
  return useQuery({
    queryKey: clientId == null ? ['expenses', 'client', null] : CLIENT_KEY(clientId, page),
    enabled: clientId != null,
    queryFn: async (): Promise<Paginated<ExpenseData>> => {
      const { data } = await api.get<Paginated<ExpenseData>>(`/v1/clients/${clientId}/expenses`, {
        params: { page },
      });
      return data;
    },
  });
}

export type ExpenseInput = {
  vendor?: string;
  description: string;
  amount_cents: number;
  spent_on: string;
  category?: string;
  billable: boolean;
  project_id?: number | null;
  receipt?: File | null;
};

function toFormData(input: ExpenseInput): FormData {
  const fd = new FormData();
  if (input.vendor) fd.append('vendor', input.vendor);
  fd.append('description', input.description);
  fd.append('amount_cents', String(input.amount_cents));
  fd.append('spent_on', input.spent_on);
  if (input.category) fd.append('category', input.category);
  fd.append('billable', input.billable ? '1' : '0');
  if (input.project_id != null) fd.append('project_id', String(input.project_id));
  if (input.receipt) fd.append('receipt', input.receipt);
  return fd;
}

export function useCreateExpense(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ExpenseInput): Promise<ExpenseData> => {
      await primeCsrf();
      const { data } = await api.post<ExpenseData>(
        `/v1/clients/${clientId}/expenses`,
        toFormData(input),
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expenses', 'client', clientId] });
      qc.invalidateQueries({ queryKey: ['billable', clientId] });
    },
  });
}

export function useUpdateExpense(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: number; input: ExpenseInput }): Promise<ExpenseData> => {
      await primeCsrf();
      // Laravel form requests read multipart PATCH via a spoofed method.
      const fd = toFormData(input);
      fd.append('_method', 'PATCH');
      const { data } = await api.post<ExpenseData>(`/v1/clients/${clientId}/expenses/${id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expenses', 'client', clientId] });
      qc.invalidateQueries({ queryKey: ['billable', clientId] });
    },
  });
}

export function useDeleteExpense(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/expenses/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expenses', 'client', clientId] });
      qc.invalidateQueries({ queryKey: ['billable', clientId] });
    },
  });
}

export type ClientBillable = {
  time_entries: TimeEntryData[];
  expenses: ExpenseData[];
};

/**
 * Unbilled work that can be pulled onto an invoice. `invoiceId` includes the
 * items already reserved onto the draft being edited, so re-opening a draft
 * still surfaces its own attached time and expenses.
 */
export function useClientBillable(clientId: number, invoiceId: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['billable', clientId, invoiceId ?? null],
    enabled,
    queryFn: async (): Promise<ClientBillable> => {
      const { data } = await api.get<ClientBillable>(`/v1/clients/${clientId}/billable`, {
        params: invoiceId ? { invoice: invoiceId } : undefined,
      });
      return data;
    },
  });
}
